// Purpose: admin user table. Promote / demote / (de)activate actions,
// all gated on MFA via the global 428 interceptor. Self-protection: the
// current admin cannot promote/demote or deactivate themselves.
//
// Phase 7.5 port: PageHeader + DataTable + Badge for status + Modal
// primitives.

import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.tsx';
import type { AdminUserSummary, Role } from '../../api/admin-users.ts';
import {
  changeUserRole,
  deactivateUser,
  listUsers,
  reactivateUser,
} from '../../api/admin-users.ts';
import { formatDateTime } from '../../lib/format.ts';
import { PageHeader } from '../../components/nav/PageHeader.tsx';
import {
  Badge,
  Button,
  DataTable,
  Modal,
  Select,
  Textarea,
  type Column,
} from '../../components/ui/index.ts';

export function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState<AdminUserSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [roleTarget, setRoleTarget] = useState<AdminUserSummary | null>(null);
  const [newRole, setNewRole] = useState<Role>('USER');
  const [deactTarget, setDeactTarget] = useState<AdminUserSummary | null>(null);
  const [deactReason, setDeactReason] = useState('');
  const [working, setWorking] = useState(false);

  const load = async (): Promise<void> => {
    try {
      setRows(await listUsers());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onChangeRole = async (): Promise<void> => {
    if (!roleTarget) return;
    setWorking(true);
    try {
      await changeUserRole(roleTarget.id, newRole);
      setRoleTarget(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onDeactivate = async (): Promise<void> => {
    if (!deactTarget) return;
    if (deactReason.trim().length < 3) {
      setError('Reason must be at least 3 characters.');
      return;
    }
    setWorking(true);
    try {
      await deactivateUser(deactTarget.id, deactReason.trim());
      setDeactTarget(null);
      setDeactReason('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onReactivate = async (row: AdminUserSummary): Promise<void> => {
    setWorking(true);
    try {
      await reactivateUser(row.id);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const columns: Column<AdminUserSummary>[] = [
    {
      key: 'user',
      header: 'User',
      cell: (row) => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-body-m text-fg-primary truncate">{row.displayName}</span>
          <span className="text-body-s text-fg-muted truncate">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      width: '120px',
      cell: (row) => <span className="text-body-s text-fg-primary">{row.role}</span>,
    },
    {
      key: 'active',
      header: 'Active',
      width: '100px',
      cell: (row) =>
        row.active ? <Badge tone="success">Yes</Badge> : <Badge tone="danger">No</Badge>,
    },
    {
      key: 'created',
      header: 'Created',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-body-s text-fg-muted">{formatDateTime(row.createdAt)}</span>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last login',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-body-s text-fg-muted">{formatDateTime(row.lastLoginAt)}</span>
      ),
    },
    {
      key: 'shares',
      header: 'Shares',
      width: '80px',
      hideOnMobile: true,
      cell: (row) => <span className="text-body-s text-fg-secondary">{row.activeShareCount}</span>,
    },
    {
      key: 'dropboxes',
      header: 'Dropboxes',
      width: '100px',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-body-s text-fg-secondary">{row.activeDropboxCount}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '260px',
      cell: (row) => {
        const isSelf = row.id === user?.id;
        if (isSelf) {
          return <span className="text-body-s text-fg-muted">self</span>;
        }
        return (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setRoleTarget(row);
                setNewRole(row.role === 'ADMIN' ? 'USER' : 'ADMIN');
              }}
            >
              Change role
            </Button>
            {row.active ? (
              <Button variant="danger" size="sm" onClick={() => setDeactTarget(row)}>
                Deactivate
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => void onReactivate(row)}>
                Reactivate
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Users" />

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      <DataTable<AdminUserSummary>
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.id}
        caption="Admin users"
      />

      <Modal
        open={!!roleTarget}
        onClose={() => setRoleTarget(null)}
        title={`Change role for ${roleTarget?.displayName ?? ''}`}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setRoleTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={working}
              onClick={() => void onChangeRole()}
            >
              Save
            </Button>
          </>
        }
      >
        <Select
          label="New role"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value as Role)}
        >
          <option value="USER">USER</option>
          <option value="ADMIN">ADMIN</option>
          <option value="READONLY">READONLY</option>
        </Select>
      </Modal>

      <Modal
        open={!!deactTarget}
        onClose={() => setDeactTarget(null)}
        title={`Deactivate ${deactTarget?.displayName ?? ''}`}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setDeactTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={working}
              disabled={deactReason.trim().length < 3}
              onClick={() => void onDeactivate()}
            >
              Deactivate
            </Button>
          </>
        }
      >
        <p>
          Deactivate the user. All of their sessions will be revoked and future logins refused.
          History is preserved.
        </p>
        <Textarea
          className="mt-3"
          rows={3}
          value={deactReason}
          onChange={(e) => setDeactReason(e.target.value)}
          placeholder="Reason (≥3 chars)"
        />
      </Modal>
    </div>
  );
}
